import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CheckCircle2, HeartPulse, Loader2, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/auth";
import { logError } from "@/lib/log";
import { CourtesyMasks } from "@/components/CourtesyMasks";

const COLOR_HEX = {
  AMARILLO: "#FACC15",
  NARANJA: "#FB923C",
  ROSA: "#F472B6",
  ROJO: "#EF4444",
  MORADO: "#A855F7",
  AZUL: "#3B82F6",
  VERDE: "#22C55E",
  NEGRO: "#18181B",
};

const formatDateEs = (iso) => {
  if (!iso || iso.length < 10) return iso || "";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};

const Dot = ({ color, size = 9 }) => (
  <span
    className="inline-block rounded-full border border-zinc-300 shrink-0"
    style={{
      width: size,
      height: size,
      backgroundColor: COLOR_HEX[color] || "#A1A1AA",
    }}
    aria-hidden
  />
);

/** Mascarillas RCP en el portal del instructor: confirmar la recepción de
 *  los envíos que le hace el gestor (y corregir cantidades si llegan menos). */
export const InstructorMasksPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null); // envío a confirmar
  const [qty, setQty] = useState({});
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await adminApi.get("/instructor/masks/shipments");
      setData(res.data);
    } catch (e) {
      logError("No se pudieron cargar los envíos de mascarillas", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const shipments = data?.shipments || [];
  const pending = useMemo(
    () => shipments.filter((s) => s.status !== "confirmed"),
    [shipments]
  );
  const confirmed = useMemo(
    () => shipments.filter((s) => s.status === "confirmed"),
    [shipments]
  );

  const openConfirm = (s) => {
    setTarget(s);
    setQty(
      Object.fromEntries((s.items || []).map((i) => [i.color, String(i.qty)]))
    );
    setNote("");
  };

  const total = useMemo(
    () =>
      Object.values(qty).reduce(
        (acc, v) => acc + (parseInt(v, 10) > 0 ? parseInt(v, 10) : 0),
        0
      ),
    [qty]
  );

  const doConfirm = async () => {
    if (!target) return;
    setSaving(true);
    try {
      await adminApi.post(
        `/instructor/masks/shipments/${target.id}/confirm`,
        {
          items: (target.items || []).map((i) => ({
            color: i.color,
            qty: parseInt(qty[i.color], 10) || 0,
          })),
          note: note.trim(),
        }
      );
      toast.success(`Recepción confirmada: ${total} mascarillas.`);
      setTarget(null);
      await fetchData();
    } catch (e) {
      const msg = e.response?.data?.detail || "No se pudo confirmar el envío.";
      toast.error(typeof msg === "string" ? msg : "Error al confirmar");
    } finally {
      setSaving(false);
    }
  };

  if (loading || shipments.length === 0) return null;

  return (
    <section
      className="bg-white border border-zinc-200 rounded-lg p-5 md:p-6 space-y-4"
      data-testid="instructor-masks-panel"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-zinc-500">
            <HeartPulse className="h-3.5 w-3.5" />
            Mascarillas RCP
          </div>
          <h3 className="heading-font text-xl font-bold text-zinc-950 mt-1">
            Envíos recibidos
          </h3>
        </div>
        {pending.length > 0 && (
          <span
            className="px-3 py-1 rounded-full bg-amber-100 text-amber-900 text-xs font-bold"
            data-testid="instructor-masks-pending-count"
          >
            {pending.length} pendiente{pending.length === 1 ? "" : "s"} de confirmar
          </span>
        )}
      </div>

      <CourtesyMasks data={data} onChanged={fetchData} />

      <ul className="space-y-2">
        {[...pending, ...confirmed].map((s) => {
          const isPending = s.status !== "confirmed";
          const items = isPending ? s.items : s.received_items;
          const missing = !isPending ? (s.total || 0) - (s.received_total || 0) : 0;
          return (
            <li
              key={s.id}
              className={`rounded-md border p-3 flex flex-col md:flex-row md:items-center gap-3 ${
                isPending ? "border-amber-300 bg-amber-50" : "border-zinc-200"
              }`}
              data-testid={`instructor-masks-shipment-${s.id}`}
            >
              <div className="md:w-40 shrink-0">
                <div className="text-sm font-mono text-zinc-700">
                  {formatDateEs(s.date)}
                </div>
                <div className="text-xs font-bold text-zinc-950">
                  {isPending ? s.total : s.received_total} uds
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  {(items || []).map((it) => (
                    <span
                      key={it.color}
                      className="inline-flex items-center gap-1.5 text-xs text-zinc-700"
                    >
                      <Dot color={it.color} />
                      <span className="font-mono font-semibold">{it.qty}</span>
                      {it.color}
                    </span>
                  ))}
                </div>
                {(s.note || missing > 0) && (
                  <div className="mt-1 text-xs text-zinc-500">
                    {s.note}
                    {s.note && missing > 0 ? " · " : ""}
                    {missing > 0 && (
                      <span className="text-amber-700 font-semibold">
                        faltaron {missing} de {s.total}
                      </span>
                    )}
                  </div>
                )}
              </div>
              {isPending ? (
                <Button
                  onClick={() => openConfirm(s)}
                  className="h-10 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shrink-0"
                  data-testid={`instructor-masks-confirm-${s.id}`}
                >
                  <PackageCheck className="h-4 w-4 mr-2" />
                  Confirmar recepción
                </Button>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 shrink-0">
                  <CheckCircle2 className="h-4 w-4" />
                  Recibido
                </span>
              )}
            </li>
          );
        })}
      </ul>

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent
          className="sm:max-w-lg"
          data-testid="instructor-masks-confirm-dialog"
        >
          <DialogHeader>
            <DialogTitle className="heading-font text-2xl">
              Confirmar recepción
            </DialogTitle>
            <DialogDescription>
              Envío del {formatDateEs(target?.date)}. Ajusta las cantidades si
              has recibido menos de lo enviado.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {(target?.items || []).map((i) => (
                <div key={i.color}>
                  <label
                    htmlFor={`recv-${i.color}`}
                    className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] font-semibold text-zinc-600 mb-1.5"
                  >
                    <Dot color={i.color} />
                    {i.color}
                    <span className="text-zinc-400 normal-case tracking-normal">
                      (enviadas {i.qty})
                    </span>
                  </label>
                  <Input
                    id={`recv-${i.color}`}
                    type="number"
                    min="0"
                    max={i.qty}
                    inputMode="numeric"
                    value={qty[i.color] ?? ""}
                    onChange={(e) =>
                      setQty((q) => ({ ...q, [i.color]: e.target.value }))
                    }
                    className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0 font-mono"
                    data-testid={`instructor-masks-recv-${i.color}`}
                  />
                </div>
              ))}
            </div>
            <div className="text-xs text-zinc-600">
              Total recibido:{" "}
              <span
                className="font-mono font-bold text-zinc-950"
                data-testid="instructor-masks-recv-total"
              >
                {total}
              </span>{" "}
              de {target?.total}
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Observaciones (opcional)
              </label>
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Caja abierta, faltaban unidades…"
                className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                data-testid="instructor-masks-recv-note"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setTarget(null)}
              className="border border-zinc-300"
              data-testid="instructor-masks-recv-cancel"
            >
              Cancelar
            </Button>
            <Button
              onClick={doConfirm}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
              data-testid="instructor-masks-recv-save"
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default InstructorMasksPanel;
